import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { branchIfNullish } from 'ad-hok-utils'
import { UserWithImages } from 'shared/src/api/types'
import { addApi } from 'shared/src/api'
import { trackOutgoingClick } from 'shared/src/utils/analytics'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import instagramIcon from 'assets/social-instagram.svg'
import tiktokIcon from 'assets/social-tiktok.svg'
import amazonIcon from 'assets/social-amazon.svg'
import pinterestIcon from 'assets/social-pinterest.svg'
import snapchatIcon from 'assets/social-snapchat.svg'
import spotifyIcon from 'assets/social-spotify.svg'
import goodreadsIcon from 'assets/social-goodreads.svg'
import etsyIcon from 'assets/social-etsy.svg'
import twitterIcon from 'assets/social-twitter.svg'
import youtubeIcon from 'assets/social-youtube.svg'
import homepageIcon from 'assets/social-homepage.svg'
import emailIcon from 'assets/social-email.svg'
import facebookIcon from 'assets/social-facebook.svg'
import applePodcastsIcon from 'assets/social-apple-podcasts.svg'
import { trackProfileNavigationEvent } from 'utils/analytics'

const BUTTON_SIZE = 44

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    gap: 4,
    marginLeft: 24,
    marginRight: 24,
    overflowX: 'scroll',
  },
  button: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})

interface SocialButtonProps {
  creatorId: number
  icon: string
  alt: string
  url: string | null
}

const SocialButton: FC<SocialButtonProps> = flowMax(
  addDisplayName('SocialButton'),
  branchIfNullish('url'),
  addApi,
  addHandlers({
    onClick:
      ({ api, url, creatorId }) =>
      () => {
        trackOutgoingClick(
          {
            url,
            creatorId,
          },
          api
        )
        trackProfileNavigationEvent(url, creatorId)
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ icon, alt, url, onClick, classes }) => (
    <a
      href={url}
      target="_blank"
      rel="nofollow noreferrer"
      onClick={onClick}
      className={classes.button}
    >
      <img src={icon} alt={alt} />
    </a>
  )
)

interface Props {
  creator: UserWithImages
  className?: string
}

export const SocialButtons: FC<Props> = flowMax(
  addDisplayName('SocialButtons'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    creator: {
      id,
      instagramUrl,
      tiktokUrl,
      amazonUrl,
      pinterestUrl,
      snapchatUrl,
      spotifyUrl,
      goodreadsUrl,
      etsyUrl,
      youtubeUrl,
      twitterUrl,
      homepage,
      publicEmail,
      facebookUrl,
      applePodcastsUrl,
    },
    className,
    classes,
    t,
  }) => (
    <div className={cx(classes.container, className)}>
      {instagramUrl && (
        <SocialButton
          creatorId={id}
          icon={instagramIcon}
          alt={t('profile.instagram')}
          url={instagramUrl}
        />
      )}
      {tiktokUrl && (
        <SocialButton
          creatorId={id}
          icon={tiktokIcon}
          alt={t('profile.tiktok')}
          url={tiktokUrl}
        />
      )}
      {publicEmail && (
        <SocialButton
          creatorId={id}
          icon={emailIcon}
          alt={t('profile.email')}
          url={`mailto:${publicEmail}`}
        />
      )}
      {facebookUrl && (
        <SocialButton
          creatorId={id}
          icon={facebookIcon}
          alt={t('profile.facebook')}
          url={facebookUrl}
        />
      )}
      {amazonUrl && (
        <SocialButton
          creatorId={id}
          icon={amazonIcon}
          alt={t('profile.amazon')}
          url={amazonUrl}
        />
      )}
      {pinterestUrl && (
        <SocialButton
          creatorId={id}
          icon={pinterestIcon}
          alt={t('profile.pinterest')}
          url={pinterestUrl}
        />
      )}
      {snapchatUrl && (
        <SocialButton
          creatorId={id}
          icon={snapchatIcon}
          alt={t('profile.snapchat')}
          url={snapchatUrl}
        />
      )}
      {spotifyUrl && (
        <SocialButton
          creatorId={id}
          icon={spotifyIcon}
          alt={t('profile.spotify')}
          url={spotifyUrl}
        />
      )}
      {applePodcastsUrl && (
        <SocialButton
          creatorId={id}
          icon={applePodcastsIcon}
          alt={t('profile.applePodcasts')}
          url={applePodcastsUrl}
        />
      )}
      {goodreadsUrl && (
        <SocialButton
          creatorId={id}
          icon={goodreadsIcon}
          alt={t('profile.goodreads')}
          url={goodreadsUrl}
        />
      )}
      {etsyUrl && (
        <SocialButton
          creatorId={id}
          icon={etsyIcon}
          alt={t('profile.etsy')}
          url={etsyUrl}
        />
      )}
      {youtubeUrl && (
        <SocialButton
          creatorId={id}
          icon={youtubeIcon}
          alt={t('profile.youtube')}
          url={youtubeUrl}
        />
      )}
      {twitterUrl && (
        <SocialButton
          creatorId={id}
          icon={twitterIcon}
          alt={t('profile.twitter')}
          url={twitterUrl}
        />
      )}
      {homepage && (
        <SocialButton
          creatorId={id}
          icon={homepageIcon}
          alt={t('profile.homepage')}
          url={homepage}
        />
      )}
    </div>
  )
)
